import Cookies from "js-cookie";

import type { Config } from "@packages/config";
import { CONFIG } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";

import { window } from "@packages/global";
import { CRYPTO_SERVICE, type CryptoService } from "../crypto";
import { LOGGER, type Logger } from "../logger";
import { COOKIE_SERVICE, type CookieAttributes } from "./cookie.types";
import { CookieConfigDTO } from "./dtos";

/**
 * Service for handling cookies, including encrypted storage.
 */
@Injectable(COOKIE_SERVICE)
export class CookieService {
  private readonly logger: Logger;

  /**
   * Initializes a new instance of the `CookieService` class.
   * @param loggerFactory - The logger factory for creating a logger instance.
   * @param config - The configuration object for the cookie service.
   * @param crypto - The crypto service for encryption and decryption.
   */
  constructor(
    @Inject(LOGGER) private readonly loggerFactory: Logger,
    @Inject(CONFIG) private readonly config: Config,
    @Inject(CRYPTO_SERVICE) private readonly crypto: CryptoService
  ) {
    this.logger = this.loggerFactory.create(COOKIE_SERVICE);

    window.Cookies = Cookies;
  }

  /**
   * Retrieves an item from cookies.
   *
   * @template T The type of the returned value.
   * @param name - The name of the cookie to retrieve.
   * @returns The parsed value of the cookie, or `null` if not found.
   */
  get<T>(name: string): T | null {
    const cookieValue: string | undefined = Cookies.get(name);

    if (!cookieValue) {
      return null;
    }

    this.logger.log(`[Get] ${name} raw cookie value:`, cookieValue);

    try {
      return JSON.parse(decodeURI(cookieValue)) as T;
    } catch {
      return cookieValue as unknown as T;
    }
  }

  /**
   * Saves an item into cookies.
   *
   * @template T The type of the value to save.
   * @param name - The name of the cookie.
   * @param value - The value to store in the cookie.
   * @param attributes - Optional cookie attributes.
   * @returns The original value saved in the cookie.
   */
  save<T>(name: string, value: T, attributes?: CookieAttributes): T {
    const cookieConfig = new CookieConfigDTO({
      name,
      value: typeof value === "string" ? value : JSON.stringify(value),
      attributes,
    });

    Cookies.set(
      cookieConfig.name,
      cookieConfig.value as string,
      cookieConfig.attributes
    );

    this.logger.log(`[Set] cookie: ${cookieConfig.name}`, cookieConfig);

    return value;
  }

  /**
   * Removes a cookie by its key.
   *
   * @param key - The name of the cookie to remove.
   */
  removeCookie(key: string) {
    this.logger.log("[Remove] cookie: ", key);
    const { cookieDomain } = this.config;

    if (Array.isArray(cookieDomain)) {
      this.logger.log("[Remove] cookie domain is an array: ", cookieDomain);

      for (const domain of cookieDomain) {
        this.removeCookieFromDomain(key, domain);
      }
    } else if (typeof cookieDomain === "string") {
      this.removeCookieFromDomain(key, cookieDomain);
    }
  }

  /**
   * Saves an encrypted item into cookies.
   *
   * @template T The type of the value to save.
   * @param name - The name of the cookie.
   * @param value - The value to encrypt and store in the cookie.
   * @param attributes - Optional cookie attributes.
   * @returns The encrypted value as a string, or `null` if the operation fails.
   */
  saveEncrypted<T>(
    name: string,
    value: T,
    attributes?: CookieAttributes
  ): string | null {
    try {
      const cookieValue =
        typeof value === "string" ? value : JSON.stringify(value);

      const encryptedValue = this.crypto.encrypt(
        cookieValue,
        this.config.secretKey
      );

      return this.save(name, encryptedValue, attributes);
    } catch (error) {
      this.logger.error("[Save encrypted] failed:", error);
      return null;
    }
  }

  /**
   * Retrieves and decrypts an encrypted item from cookies.
   *
   * @template T The type of the decrypted value.
   * @param name - The name of the cookie to retrieve and decrypt.
   * @returns The decrypted value, or `null` if decryption fails.
   */
  getDecrypted<T>(name: string): T | null {
    const encryptedData = this.get(name);
    if (!encryptedData) return null;

    const data =
      typeof encryptedData === "string"
        ? encryptedData
        : JSON.stringify(encryptedData);

    const decryptedData =
      this.crypto.decrypt(data, this.config.secretKey) ?? null;

    try {
      return JSON.parse(decryptedData as string) as T;
    } catch {
      return decryptedData as T;
    }
  }

  /**
   * Helper method to remove a cookie from a specific domain.
   *
   * @param key - The name of the cookie to remove.
   * @param domain - The domain from which to remove the cookie.
   */
  private removeCookieFromDomain(key: string, domain: string) {
    this.logger.log("[Remove] Before Removal:", Cookies.get());
    Cookies.remove(key, { path: "/", domain });
    Cookies.remove(key, { path: "*", domain });
    Cookies.remove(key, { path: "", domain });
    Cookies.remove(key);
    this.logger.log("[Remove] After Removal:", Cookies.get());
    this.logger.log(`[Remove] cookie ${key} from domain: ${domain}`);
  }
}
